import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CountryDialPicker } from '@/shared/ui/components/country-dial-picker';
import { useAuthStore } from '@/auth/auth-store';
import {
  useCreateStaff,
  useRolesByType,
  usePracticesList,
  buildCreateStaffBody,
} from '@/settings/services';
import { cn } from '@/lib/utils';

/**
 * Add Staff sub-wizard (PR 5 — PROD-927, epic PROD-856).
 *
 * Flutter parity: `add_edit_staff_widget.dart` (the shared add/edit shell
 * hosted inside `add_staff_widget.dart`). STEP 1 captures name + email +
 * mobile + role + practice assignment → fires POST /api/Staff. STEP 2 in
 * Flutter would continue into per-tab details; we render a confirmation
 * pane and defer the rest to Settings → Staff → Personal/Account/etc.
 * Same scope-trim pattern as PR 4.
 *
 * Wire-shape verified against working dev cURL 2026-05-13 (PROD-765
 * settings-side shipping work) — see `use-create-staff.ts` for the
 * `buildCreateStaffBody` invariants.
 *
 * Field order matches the Flutter widget:
 *   1. STAFF DETAILS
 *      - First Name *      | Last Name *
 *      - Role *            | (spacer)
 *   2. ACCOUNT DETAILS
 *      - Email Address *   | Contact Number *
 *   3. ASSIGN TO PRACTICE *  (multi-select chips — at least one required)
 */
export interface StaffAddSheetProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

type Step = 1 | 2;

interface Draft {
  firstName: string;
  lastName: string;
  emailId: string;
  mobileNumber: string;
  role: number | null;
  practiceIds: string[];
}

const EMPTY_DRAFT: Draft = {
  firstName: '',
  lastName: '',
  emailId: '',
  mobileNumber: '+971-',
  role: null,
  practiceIds: [],
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_MIN_DIGITS = 7;

export function StaffAddSheet({
  open,
  onClose,
  onCreated,
}: StaffAddSheetProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [createdName, setCreatedName] = useState('');
  // Tracks whether a successful create happened — list invalidation defers
  // to Sheet onClose so the underlying list doesn't re-render while STEP 2
  // is visible (same pattern as PR 4 / PROD-872). Uses a ref instead of
  // state because the unmount-cleanup useEffect below needs the latest
  // value (Samer review PR #332 — unmount race fallback: if the Sheet is
  // closed by parent nav before handleSheetClose fires, the cleanup
  // invalidates so the list never gets stranded stale).
  const didCreateRef = useRef(false);

  const createMutation = useCreateStaff();
  const { data: roleRows = [] } = useRolesByType(2, orgId);
  const { data: practices = [] } = usePracticesList(orgId, false);

  const roleOptions = useMemo(
    () =>
      roleRows
        .filter((r) => !r.isArchived)
        .map((r) => ({ value: r.roleId, label: r.roleName })),
    [roleRows],
  );

  // Reset on every open transition.
  useEffect(() => {
    if (open) {
      setStep(1);
      setDraft(EMPTY_DRAFT);
      setCreatedName('');
      didCreateRef.current = false;
    }
  }, [open]);

  // Unmount safety net (Samer review PR #332) — if the component unmounts
  // without `handleSheetClose` firing (e.g. parent route change), the
  // pending list invalidation would silently drop. Fire it here on
  // teardown so the list never stays stale after a successful create.
  // queryClient is a stable singleton across the app — safe to depend on.
  useEffect(() => {
    return () => {
      if (didCreateRef.current) {
        queryClient.invalidateQueries({ queryKey: ['settings', 'staff-list'] });
      }
    };
  }, [queryClient]);

  const handleSheetClose = () => {
    if (didCreateRef.current) {
      queryClient.invalidateQueries({ queryKey: ['settings', 'staff-list'] });
      // Clear the ref so the unmount cleanup doesn't double-invalidate.
      // RQ would dedupe this anyway, but cheaper to short-circuit.
      didCreateRef.current = false;
    }
    onClose();
  };

  const phoneDigits = draft.mobileNumber.split('-')[1] ?? '';
  const isValid =
    draft.firstName.trim().length > 0 &&
    draft.lastName.trim().length > 0 &&
    EMAIL_REGEX.test(draft.emailId.trim()) &&
    phoneDigits.replace(/\D/g, '').length >= PHONE_MIN_DIGITS &&
    draft.role !== null &&
    draft.practiceIds.length > 0;

  const isSaving = createMutation.isPending;

  const handleSave = () => {
    if (step === 2) {
      handleSheetClose();
      return;
    }
    if (!isValid || !orgId || draft.role === null) return;

    const body = buildCreateStaffBody({
      organizationId: orgId,
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      emailId: draft.emailId.trim(),
      mobileNumber: draft.mobileNumber,
      role: draft.role,
      practiceIds: draft.practiceIds,
    });

    // Defensive post-mapper guard (Samer review PR #332) — validation
    // already checks `draft.practiceIds.length > 0`, but verify the wire
    // body too in case the mapper ever filters something out. Backend
    // 400s with a generic error if `assignStaffToPractice: []`.
    if (body.assignStaffToPractice.length === 0) {
      toast.error('At least one practice assignment is required.');
      return;
    }

    createMutation.mutate(body, {
      onSuccess: () => {
        const fullName = `${draft.firstName.trim()} ${draft.lastName.trim()}`.trim();
        setCreatedName(fullName);
        setStep(2);
        didCreateRef.current = true;
        toast.success(`Invited ${fullName}`);
        onCreated?.();
      },
      onError: (err) =>
        toast.error(
          err instanceof Error ? err.message : 'Could not create staff.',
        ),
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleSheetClose()}>
      <SheetContent
        side="right"
        className="w-full max-w-[720px] sm:max-w-[720px] overflow-y-auto scrollbar-hide p-0"
      >
        <SheetHeader className="flex flex-row items-center justify-between px-6 pt-6 pb-3 space-y-0">
          <button
            type="button"
            className="text-sm font-semibold text-[var(--accent-primary)] hover:underline disabled:opacity-50"
            onClick={handleSheetClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <SheetTitle className="[font:var(--type-card-title)] text-[var(--ink-body)]">
            Add Staff
          </SheetTitle>
          <button
            type="button"
            className="text-sm font-semibold text-[var(--accent-primary)] hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={(step === 1 && (!isValid || isSaving)) || isSaving}
          >
            {step === 1 ? (isSaving ? 'Saving…' : 'Save') : 'Done'}
          </button>
        </SheetHeader>

        <StepIndicator step={step} />

        {step === 1 ? (
          <StepOnePane
            draft={draft}
            setDraft={setDraft}
            roleOptions={roleOptions}
            practices={practices}
          />
        ) : (
          <StepTwoPane createdName={createdName} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function StepIndicator({ step }: { step: Step }) {
  return (
    <div className="flex gap-2 px-6 pb-4">
      <StepChip
        active={step === 1}
        past={step > 1}
        number="STEP 1"
        label="Add staff details"
      />
      <StepChip
        active={step === 2}
        past={false}
        number="STEP 2"
        label="Done"
      />
    </div>
  );
}

function StepChip({
  active,
  past,
  number,
  label,
}: {
  active: boolean;
  past: boolean;
  number: string;
  label: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-1 items-center gap-2 rounded-[8px] border-2 px-3 py-2',
        active
          ? 'border-[var(--color-blue-500)]'
          : 'border-[var(--surface-line)]',
      )}
    >
      <div
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
          active
            ? 'bg-[var(--color-blue-500)] text-white'
            : past
              ? 'bg-[var(--color-status-success)] text-white'
              : 'bg-[var(--color-grey-100)] text-[var(--ink-meta)]',
        )}
      >
        {past ? <Check className="h-4 w-4" aria-hidden /> : number.slice(-1)}
      </div>
      <div className="flex flex-col leading-tight">
        <span
          className={cn(
            'text-xs font-medium',
            active
              ? 'text-[var(--accent-primary)]'
              : 'text-[var(--ink-meta)]',
          )}
        >
          {number}
        </span>
        <span
          className={cn(
            'text-sm font-semibold',
            active
              ? 'text-[var(--ink-body)]'
              : 'text-[var(--ink-meta)]',
          )}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

function StepOnePane({
  draft,
  setDraft,
  roleOptions,
  practices,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  roleOptions: Array<{ value: number; label: string }>;
  practices: ReturnType<typeof usePracticesList>['data'];
}) {
  return (
    <div className="px-6 pb-10 space-y-6">
      <div className="rounded-card bg-[var(--color-surface-background)] p-4 text-center">
        <p className="[font:var(--type-body-strong)] text-[var(--accent-primary)]">
          You&rsquo;re Creating An Account For A Staff Member
        </p>
        <p className="mt-2 [font:var(--type-body)] text-[var(--ink-meta)]">
          After clicking &lsquo;Save&rsquo; Scrips will send an invite to this user
          so they can log into the practice and add their details
        </p>
      </div>

      <section>
        <h3 className="text-xs font-medium text-[var(--accent-primary)] mb-3">
          Staff details
        </h3>
        <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FieldText
            label="First name"
            required
            value={draft.firstName}
            onChange={(v) => setDraft({ ...draft, firstName: v })}
          />
          <FieldText
            label="Last name"
            required
            value={draft.lastName}
            onChange={(v) => setDraft({ ...draft, lastName: v })}
          />
          <RoleField
            options={roleOptions}
            value={draft.role}
            onChange={(v) => setDraft({ ...draft, role: v })}
          />
        </div>
      </section>

      <section>
        <h3 className="text-xs font-medium text-[var(--accent-primary)] mb-3">
          Account details
        </h3>
        <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FieldText
            label="Email address"
            required
            type="email"
            value={draft.emailId}
            onChange={(v) => setDraft({ ...draft, emailId: v })}
          />
          <div className="flex flex-col gap-2">
            <Label text="Contact number" required />
            <CountryDialPicker
              value={draft.mobileNumber}
              onChange={(v) => setDraft({ ...draft, mobileNumber: v })}
              size="lg"
            />
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xs font-medium text-[var(--accent-primary)] mb-3">
          Assign to practice
        </h3>
        <PracticesPicker
          practices={practices ?? []}
          value={draft.practiceIds}
          onChange={(next) => setDraft({ ...draft, practiceIds: next })}
        />
      </section>
    </div>
  );
}

function StepTwoPane({ createdName }: { createdName: string }) {
  return (
    <div className="px-6 pb-10 pt-6 space-y-6">
      <div className="rounded-card border border-[var(--status-good)] bg-[var(--color-status-success-bg)] p-6">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-full bg-[var(--color-status-success)] flex items-center justify-center flex-shrink-0">
            <Check className="h-5 w-5 text-white" aria-hidden />
          </div>
          <div>
            <p className="[font:var(--type-card-title)] text-[var(--status-good-text)]">
              {createdName || 'Staff member'} added
            </p>
            <p className="mt-1 [font:var(--type-body)] text-[var(--status-good-text)]">
              An invite email has been sent. They&rsquo;ll appear in the staff
              list shortly.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-card border border-dashed border-[var(--surface-line)] bg-[var(--surface-read)] px-6 py-6 text-center">
        <p className="text-sm font-semibold text-[var(--ink-body)]">
          Configure deeper details later
        </p>
        <p className="mt-2 [font:var(--type-body)] text-[var(--ink-meta)] max-w-md mx-auto">
          Personal info, professional details, schedule, and additional
          practice assignments can be configured from{' '}
          <span className="font-semibold text-[var(--ink-body)]">
            Settings → Staff → {createdName || 'this staff member'}
          </span>{' '}
          once the wizard finishes.
        </p>
      </div>
    </div>
  );
}

function FieldText({
  label,
  required,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'email';
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label text={label} required={required} />
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10"
      />
    </div>
  );
}

function Label({ text, required }: { text: string; required?: boolean }) {
  return (
    <label className="text-xs font-medium text-[var(--ink-meta)]">
      {text}
      {required && (
        <span className="ml-1 text-[var(--color-status-error)]">*</span>
      )}
    </label>
  );
}

function RoleField({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: number; label: string }>;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <div className="flex flex-col gap-2">
      <Label text="Role" required />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <button
              type="button"
              className={cn(
                'h-10 flex items-center justify-between rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] px-3 text-sm',
                selected
                  ? 'text-[var(--ink-body)]'
                  : 'text-[var(--color-text-hint)]',
              )}
            />
          }
        >
          <span className="truncate">{selected?.label ?? 'Select role'}</span>
          {selected ? (
            <X
              className="h-4 w-4 text-[var(--ink-meta)] hover:text-[var(--ink-body)] cursor-pointer flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              aria-label="Clear role"
            />
          ) : (
            <ChevronDown className="h-4 w-4 text-[var(--ink-meta)] flex-shrink-0" />
          )}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-1"
        >
          {options.length === 0 ? (
            <p className="px-3 py-2 [font:var(--type-body)] text-[var(--ink-meta)]">
              No roles available
            </p>
          ) : (
            options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center px-3 py-2 text-left text-sm rounded',
                  o.value === value
                    ? 'bg-[var(--accent-active-bg)] text-[var(--accent-active-text)]'
                    : 'hover:bg-[var(--color-surface-background)] text-[var(--ink-body)]',
                )}
              >
                {o.label}
              </button>
            ))
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function PracticesPicker({
  practices,
  value,
  onChange,
}: {
  practices: Array<{ practiceSetupId: string | null; practiceName: string | null }>;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const valueSet = new Set(value);

  const toggle = (id: string) => {
    if (valueSet.has(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  };

  if (practices.length === 0) {
    return (
      <div className="rounded-card border border-dashed border-[var(--surface-line)] bg-[var(--surface-read)] px-6 py-6 text-center [font:var(--type-body)] text-[var(--ink-meta)]">
        No practices configured yet — go back to STEP 1 (Add Practice Details)
        and add at least one practice before assigning staff.
      </div>
    );
  }

  return (
    <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] p-3 space-y-1">
      {practices.map((p) => {
        const id = p.practiceSetupId ?? '';
        if (!id) return null;
        const checked = valueSet.has(id);
        return (
          <label
            key={id}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded cursor-pointer transition-colors',
              checked
                ? 'bg-[var(--accent-active-bg)]'
                : 'hover:bg-[var(--color-surface-background)]',
            )}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(id)}
              className="h-4 w-4 accent-[var(--color-blue-500)]"
            />
            <span className="text-sm text-[var(--ink-body)]">
              {p.practiceName ?? 'Untitled practice'}
            </span>
          </label>
        );
      })}
    </div>
  );
}
